import { InfiniteData, UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';

import {
  UpdateGroupMutation,
  UpdateGroupMutationVariables,
  GetFollowedQuery,
  GroupStatus,
  useGetFollowedQuery,
  BootstrapQuery,
  UserProfileFragment,
} from 'api';
import { useNotify, useCurrentUser, useBootstrapQueryKey } from 'hooks';
import { FOLLOW_QUERIES_LIMIT } from 'app-constants';

type Mutation = UpdateGroupMutation;
type MutationVariables = UpdateGroupMutationVariables;

type FollowedQueryData = InfiniteData<GetFollowedQuery>;
type BootstrapQueryData = BootstrapQuery;

const useCreateGroup = () => {
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const followedQueryKey = useGetFollowedQuery.getKey({
    data: { user_id: currentUser.id, limit: FOLLOW_QUERIES_LIMIT },
  });
  const bootstrapQueryKey = useBootstrapQueryKey();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(followedQueryKey);
      await queryClient.cancelQueries(bootstrapQueryKey);

      const baseFollowedState = queryClient.getQueryData<FollowedQueryData>(followedQueryKey);
      const baseBootstrapState = queryClient.getQueryData<BootstrapQueryData>(bootstrapQueryKey);

      const { id, group_status } = mutationArgs.data;
      const isDeleted = group_status === GroupStatus.Deleted;

      const nextFollowedState = produce(baseFollowedState, draftState => {
        draftState.pages.map(page => {
          const { feed_follows } = page.getPaginatedUserFeedFollows;
          const index = feed_follows.findIndex(feedFollow => feedFollow.group?.id === id);

          if (index === -1) {
            return page;
          }

          isDeleted
            ? feed_follows.splice(index, 1)
            : (feed_follows[index] = {
                ...feed_follows[index],
                ...mutationArgs.data,
              });

          return page;
        });
      });

      const nextBootstrapState = produce(baseBootstrapState, draftState => {
        const { group_moderator } = draftState.bootstrap.user as UserProfileFragment;

        // Check for not-null group_moderator as it's possible to not be a group moderator
        if (group_moderator) {
          const index = group_moderator.findIndex(({ group_id }) => group_id === id);
          group_moderator.splice(index, 1);
        }
      });

      if (baseFollowedState) {
        queryClient.setQueryData<FollowedQueryData>(followedQueryKey, nextFollowedState);
      }

      if (baseBootstrapState) {
        queryClient.setQueryData<BootstrapQueryData>(bootstrapQueryKey, nextBootstrapState);
      }

      return { baseFollowedState, baseBootstrapState };
    },

    onError: (error, mutationArgs, { baseFollowedState, baseBootstrapState }) => {
      if (baseFollowedState) {
        queryClient.setQueryData<FollowedQueryData>(followedQueryKey, baseFollowedState);
      }

      if (baseBootstrapState) {
        queryClient.setQueryData<BootstrapQueryData>(bootstrapQueryKey, baseBootstrapState);
      }

      notify.mutationError();
    },
  };

  return mutationOptions;
};

export default useCreateGroup;
